/**
 * Попап «Оберти вже твої» після покупки пакета (owner 2026-09-24).
 *
 * Оплата йде у WayForPay поза застосунком, а оберти нараховує вебхук. Тому
 * після натискання на пакет ставимо позначку «чекаємо оплату» і 20 хвилин
 * опитуємо стан колеса; щойно сервер віддає нову оплачену покупку —
 * переносимо людину в розділ колеса і показуємо попап там. Без позначки
 * запит не робимо: стан колеса й так приходить з головної.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { markWheelPurchaseSeen, useWheelState } from "@/api/hooks";
import { WheelPurchasedPopup } from "@/components/WheelPurchasedPopup";
import { tg } from "@/lib/telegram";

const PENDING_KEY = "wheel_purchase_pending_at";
const SEEN_KEY = "wheel_purchase_seen";
const PENDING_MS = 20 * 60 * 1000;

function readNum(key: string): number {
  try {
    return Number(localStorage.getItem(key) || 0);
  } catch {
    return 0;
  }
}

/** Чи покаже вотчер попап «Оберти вже твої» для цієї покупки. Сторінка
 *  колеса тоді не відкриває свій попап призу поверх — два попапи підряд
 *  власник не приймає (owner 2026-09-05). */
export function purchasePopupPending(orderReference: string | null | undefined): boolean {
  if (!orderReference) return false;
  try {
    return localStorage.getItem(SEEN_KEY) !== orderReference;
  } catch {
    return true;
  }
}

/** Викликати перед відкриттям оплати пакета. */
export function markWheelPurchasePending(): void {
  try {
    localStorage.setItem(PENDING_KEY, String(Date.now()));
  } catch {
    /* приватний режим — опитування просто не ввімкнеться */
  }
  window.dispatchEvent(new Event("wheel:purchase-pending"));
}

export function WheelPurchaseWatcher() {
  const [pending, setPending] = useState(
    () => Date.now() - readNum(PENDING_KEY) < PENDING_MS,
  );
  const state = useWheelState({
    enabled: pending,
    refetchInterval: pending ? 4000 : false,
  });
  const [open, setOpen] = useState<{ order: string; spins: number } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const on = () => setPending(true);
    window.addEventListener("wheel:purchase-pending", on);
    return () => window.removeEventListener("wheel:purchase-pending", on);
  }, []);

  // Опитування живе не довше 20 хвилин від натискання.
  useEffect(() => {
    if (!pending) return;
    const left = PENDING_MS - (Date.now() - readNum(PENDING_KEY));
    const t = setTimeout(() => setPending(false), Math.max(0, left));
    const onVis = () => {
      if (document.visibilityState === "visible") void state.refetch();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [pending, state]);

  const purchase = state.data?.purchase;
  useEffect(() => {
    if (!purchase || open) return;
    let seen = "";
    try {
      seen = localStorage.getItem(SEEN_KEY) || "";
    } catch {
      /* ignore */
    }
    if (seen === purchase.order_reference) return;
    // Попап живе в розділі колеса (owner 2026-09-24: «не на главную кидать,
    // а в разделе колеса оставаться»): після оплати застосунок часто
    // відкривається наново з головної — переносимо людину до колеса.
    if (location.pathname !== "/wheel") navigate("/wheel");
    setOpen({ order: purchase.order_reference, spins: purchase.spins });
    setPending(false);
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch {
      /* ignore */
    }
    tg()?.HapticFeedback?.notificationOccurred?.("success");
  }, [purchase, open, location.pathname, navigate]);

  const close = () => {
    if (!open) return;
    try {
      localStorage.setItem(SEEN_KEY, open.order);
    } catch {
      /* ignore */
    }
    void markWheelPurchaseSeen(open.order).catch(() => {});
    setOpen(null);
    void state.refetch();
  };

  return (
    <WheelPurchasedPopup
      open={open !== null}
      spins={open?.spins ?? 0}
      total={state.data?.spins_left ?? open?.spins ?? 0}
      onClose={close}
      onSpin={close}
    />
  );
}
